import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  SwipeableDrawer,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useParams, useNavigate, useLocation, Navigate } from "react-router-dom";
import Breadcrumb from "../../components/common/Breadcrumb/Breadcrumb";
import MedicalCampaignHeader from "../../components/common/MedicalCampaignDetailPageComponents/MedicalCampaignHeader";
import DonationSlider from "../../components/common/DonationSlider";
import MedicalStory from "../../components/common/MedicalCampaignDetailPageComponents/MedicalStory";
import DocumentSection from "../../components/common/CampaignDetailPageComponents/Document";
import Updates from "../../components/common/CampaignDetailPageComponents/Updates";
import Comments from "../../components/common/CampaignDetailPageComponents/Comments";
import UrgentNeedsCampaign from "../../components/common/MedicalCampaignDetailPageComponents/UrgentNeedsCampaign";
import Faq from "../../components/common/CampaignDetailPageComponents/Faq";
import NewsLogoSection from "../HomePage/NewsLogoSection";
import CampaignPopup from "../../components/common/CampaignDetailPageComponents/CampaignPopup";
import DonationBoxV2 from "../../components/common/DonationBoxV2";
import HowToDonateV2 from "../../components/common/CampaignDetailPageComponents/HowToDonateV2";
import { getCampaignBySlug, getCampaignById } from "../../api/services/campaignService";
import { getLifePrimarySlugFromRedirects } from "../../utils/slugUtils";
import { Images } from "../../utils/Images";

const BRAND_RED = "#F04951";
// Active section-tab underline (Figma node 798:6441)
const TAB_UNDERLINE_RED = "#F04951";
// iOS needs different SwipeableDrawer transition handling for a smooth gesture.
const iOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

// Section tabs (Figma node 798:6441) — Story | Document | Updates | Comments.
// Same icon assets and same order the design shows; Document/Updates render
// nothing when the campaign has no rows (both components return null), so the
// tab simply scrolls to an empty spot rather than a broken section.
const SECTION_TABS = [
  { key: "story", label: "Story", icon: Images.box },
  { key: "document", label: "Document", icon: Images.layer },
  { key: "updates", label: "Updates", icon: Images.refresh },
  { key: "comments", label: "Comments", icon: Images.messages },
];

const BREADCRUMB_ITEMS = [
  { label: "Campaigns", path: "/campaigns" },
  { label: "Story", path: null },
];

/**
 * Story campaign page (/story/:slug — redirect.is_story_campaign === true).
 *
 * Rebuilt to match Figma node 798-6351. Section order and presence come
 * straight from that frame:
 *   hero → recent-donors ticker → [ tabs + story + document + updates +
 *   comments | donation box (incl. how-to-donate) ] → urgent campaigns → FAQ →
 *   news logos  (newsletter + footer are global, in the layout Footer)
 *
 * Sections the design does NOT have were removed: "Refer a Friend", the
 * Top/Recent "Donors" card (the ticker under the hero already surfaces recent
 * donors), the vertical CampaignSidebar, and the legacy `showPatientHeader`
 * branch (PatientImageCard + CampaignProgress + PatientInfoSection) which was
 * dead code — its state was never set true except by the mapped-campaign
 * button, which navigates away instead.
 */
const MedicalCampaignDetailPage = ({ campaign: propCampaign }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery("(min-width: 1001px)");
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(propCampaign || null);
  const [loading, setLoading] = useState(!propCampaign);
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [donationAmount, setDonationAmount] = useState(3000);
  const [activeSection, setActiveSection] = useState("story");
  const [donationSheetOpen, setDonationSheetOpen] = useState(false);

  // Update campaign if prop changes
  useEffect(() => {
    if (propCampaign) {
      setCampaign(propCampaign);
      setLoading(false);
      setError(null);
    }
  }, [propCampaign]);

  // Fetch campaign data from API only if not provided as prop
  useEffect(() => {
    if (propCampaign) return;

    const fetchCampaign = async () => {
      try {
        setLoading(true);
        const response = await getCampaignBySlug(slug, location.pathname.startsWith("/story/"));
        setCampaign(response?.data || null);
        setError(null);
      } catch (err) {
        console.error("Error fetching campaign:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchCampaign();
    }
  }, [slug, propCampaign, location.pathname]);

  // Show popup after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowPopup(true), 3_000);
    return () => clearTimeout(timer);
  }, []);

  const handleClosePopup = useCallback(() => setShowPopup(false), []);

  // Hero "Donate Now": stay on the page — scroll to the donation box on
  // desktop, open the bottom sheet where the box isn't rendered inline.
  const handleHeroDonateClick = useCallback(() => {
    if (isDesktop) {
      document.getElementById("donation-box")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      setDonationSheetOpen(true);
    }
  }, [isDesktop]);

  const handleSectionTabClick = useCallback((key) => {
    setActiveSection(key);
    document.getElementById(key)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Scroll-spy: highlight the section currently in view (same as LifeDetailPageV2)
  useEffect(() => {
    let scrollTimeoutId = null;
    const updateActive = () => {
      let best = null;
      let bestTop = -Infinity;
      SECTION_TABS.forEach(({ key }) => {
        const el = document.getElementById(key);
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        if (top <= 140 && top > bestTop) {
          best = key;
          bestTop = top;
        }
      });
      if (best) setActiveSection(best);
    };
    const onScroll = () => {
      if (scrollTimeoutId) clearTimeout(scrollTimeoutId);
      scrollTimeoutId = setTimeout(updateActive, 80);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollTimeoutId) clearTimeout(scrollTimeoutId);
    };
  }, []);

  // "Know more" on a mapped story: life campaign → /life/:slug, else /campaign/:slug
  const handleStoryButtonClick = async () => {
    const redirectSlug = campaign?.redirects?.[0]?.campaignMapping?.redirectSlug;
    const mappedCampaignId = campaign?.redirects?.[0]?.campaignMapping?.campaignID;
    if (redirectSlug && mappedCampaignId != null) {
      try {
        const response = await getCampaignById(mappedCampaignId);
        const isLifeCampaign =
          Number(response?.data?.featureLifeCampaign) === 1 ||
          Number(response?.data?.featuremedicalCampaign) === 1;
        if (response?.data && isLifeCampaign) {
          const lifeSlug = getLifePrimarySlugFromRedirects(response.data, redirectSlug);
          navigate("/life/" + lifeSlug);
          return;
        }
      } catch (e) {
        console.error("Know more: could not load mapped campaign", e);
      }
      navigate("/campaign/" + redirectSlug, { state: { mappedCampaignId } });
      return;
    }
    if (redirectSlug) {
      navigate("/campaign/" + redirectSlug);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !campaign) {
    return <Navigate to="/campaigns" replace />;
  }

  const title = campaign?.title || "";
  const campaignId = campaign?.id;
  const beneficiaryName = campaign?.medical_details?.beneficiary_name?.trim() || "";

  const sectionTabsBar = (
    <Box
      sx={{
        background: "#FFFFFF",
        borderRadius: "12px",
        border: "1px solid #F1F1F1",
        boxShadow: "0px 4px 14px rgba(0,0,0,0.06)",
        pt: isMobile ? "14px" : "30px",
        pb: isMobile ? "10px" : "20px",
        px: isMobile ? "6px" : "30px",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: isMobile ? "space-evenly" : "space-between",
        mb: "30px",
        overflowX: "auto",
      }}
    >
      {SECTION_TABS.map(({ key, label, icon }) => {
        const isActive = activeSection === key;
        return (
          <Box
            key={key}
            onClick={() => handleSectionTabClick(key)}
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: isMobile ? "center" : "flex-start",
              gap: isMobile ? "6px" : "12px",
              height: isMobile ? "46px" : "60px",
              px: isMobile ? "4px" : "28px",
              cursor: "pointer",
              userSelect: "none",
              flex: isMobile ? "1 1 0" : "0 0 auto",
              minWidth: 0,
            }}
          >
            <Box
              component="img"
              src={icon}
              alt={label}
              sx={{ width: isMobile ? "16px" : "26px", height: isMobile ? "16px" : "26px", flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontFamily: "Red Hat Display",
                fontWeight: isActive ? 600 : 400,
                fontSize: isMobile ? "12px" : "18px",
                color: isActive ? "#1D1D1B" : "#686866",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1,
              }}
            >
              {label}
            </Typography>
            {isActive && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  height: "3px",
                  borderRadius: "2px",
                  background: TAB_UNDERLINE_RED,
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <>
      <Breadcrumb items={BREADCRUMB_ITEMS} />

      {/* ── HERO ── */}
      <MedicalCampaignHeader
        campaignData={campaign}
        donationAmount={donationAmount}
        onDonateClick={handleHeroDonateClick}
      />

      {/* ── RECENT DONORS TICKER (Figma node 798:7321) ── */}
      <DonationSlider campaignId={campaignId} />

      {/* ── TWO-COLUMN: content | donation box ── */}
      <Box
        sx={{
          width: isMobile ? "100%" : "94.44%",
          maxWidth: "1440px",
          margin: "0 auto",
          mt: isMobile ? "24px" : "30px",
          px: isMobile ? "16px" : 0,
          display: "flex",
          flexDirection: isDesktop ? "row" : "column",
          gap: isDesktop ? "30px" : 0,
          alignItems: isDesktop ? "flex-start" : "stretch",
        }}
      >
        {/* LEFT: tabs + story content */}
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          {sectionTabsBar}

          <Box id="story" sx={{ mb: "30px", scrollMarginTop: "130px" }}>
            {/* No showFullContentByDefault here — the story is collapsed with a
                "Read More" button, as on the old story page. Expanding it also
                reveals MedicalStory's "Click Here To Know About The Story"
                link (rendered on the last story block, gated on showFullContent
                and !showPatientHeader), which routes to the mapped campaign via
                onStoryButtonClick. */}
            <MedicalStory
              onStoryButtonClick={handleStoryButtonClick}
              showPatientHeader={false}
              campaignData={campaign}
            />
          </Box>

          <Box id="document" sx={{ mb: "30px", scrollMarginTop: "130px" }}>
            <DocumentSection campaignData={campaign} />
          </Box>

          <Box id="updates" sx={{ mb: "30px", scrollMarginTop: "130px" }}>
            <Updates campaignData={campaign} />
          </Box>

          <Box id="comments" sx={{ mb: "30px", scrollMarginTop: "130px" }}>
            <Comments campaignData={campaign} />
          </Box>
        </Box>

        {/* RIGHT: sticky donation box (Figma node 798:6980) */}
        {isDesktop && (
          <Box
            id="donation-box"
            sx={{
              width: "537px",
              flexShrink: 0,
              position: "sticky",
              top: "20px",
              alignSelf: "flex-start",
              scrollMarginTop: "20px",
            }}
          >
            {/* DonationBoxV2 already renders the design's How To Donate card
                (HowToDonateV2 — heading + video) as its last block, so nothing
                else belongs under it here. */}
            <DonationBoxV2
              campaignTitle={title}
              campaignId={campaignId}
              beneficiaryName={beneficiaryName}
              campaignData={campaign}
              onDonationAmountChange={setDonationAmount}
            />
          </Box>
        )}
      </Box>

      {/* ── FULL-WIDTH SECTIONS ── */}
      <Box
        sx={{
          width: isMobile ? "100%" : "94.44%",
          maxWidth: "1440px",
          margin: "0 auto",
          px: isMobile ? "16px" : 0,
        }}
      >
        <Box sx={{ mb: "15px" }}>
          <UrgentNeedsCampaign campaignData={campaign} />
        </Box>
        {/* MOBILE / TABLET: How To Donate video, after Urgent Needs (see LifeDetailPageV2). */}
        {!isDesktop && (
          <Box sx={{ mb: "15px" }}>
            <HowToDonateV2 />
          </Box>
        )}
        <Box sx={{ mb: isMobile ? 0 : "15px" }}>
          <Faq />
        </Box>
      </Box>

      {!isMobile && <NewsLogoSection />}

      <CampaignPopup isOpen={showPopup} onClose={handleClosePopup} campaignData={campaign} />

      {/* ── MOBILE/TABLET: sticky Donate Now + bottom sheet ── */}
      {!isDesktop && (
        <>
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              background: "#FFFFFF",
              borderTop: "1px solid #F0F0F0",
              padding: "12px 16px",
              paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
            }}
          >
            <Box
              onClick={() => setDonationSheetOpen(true)}
              sx={{
                width: "100%",
                height: "52px",
                background: BRAND_RED,
                borderRadius: "11px",
                boxShadow: "0px 4px 0px #141414",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontFamily: "Red Hat Display",
                fontWeight: 700,
                fontSize: "16px",
                color: "#FFFFFF",
                userSelect: "none",
              }}
            >
              Donate Now
            </Box>
          </Box>

          <SwipeableDrawer
            anchor="bottom"
            open={donationSheetOpen}
            onClose={() => setDonationSheetOpen(false)}
            onOpen={() => setDonationSheetOpen(true)}
            disableSwipeToOpen
            disableBackdropTransition={!iOS}
            disableDiscovery={iOS}
            PaperProps={{
              sx: {
                borderTopLeftRadius: "20px",
                borderTopRightRadius: "20px",
                maxHeight: "92vh",
                overflow: "auto",
              },
            }}
          >
            <Box
              sx={{
                pt: "10px",
                pb: "6px",
                display: "flex",
                justifyContent: "center",
                cursor: "grab",
                touchAction: "none",
                position: "sticky",
                top: 0,
                background: "#FFFFFF",
                zIndex: 1,
                borderTopLeftRadius: "20px",
                borderTopRightRadius: "20px",
              }}
            >
              <Box sx={{ width: "40px", height: "4px", background: "#E0E0E0", borderRadius: "2px" }} />
            </Box>
            <Box sx={{ p: "16px", pt: "6px" }}>
              <DonationBoxV2
                campaignTitle={title}
                campaignId={campaignId}
                beneficiaryName={beneficiaryName}
                campaignData={campaign}
                onDonationAmountChange={setDonationAmount}
                showHowToDonate={false}
              />
            </Box>
          </SwipeableDrawer>

          <Box sx={{ height: "80px" }} />
        </>
      )}
    </>
  );
};

export default MedicalCampaignDetailPage;
